import semver from 'semver';

import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {withLoadingScreen} from 'util/Promises';

import {
  BENCHLING_APPS_WEB_URL,
  bulkArchiveBenchlingApps,
  bulkUnarchiveBenchlingApps,
} from './BenchlingAppDAO';
import {AppDefinition} from './developer/BenchlingAppsPage';

interface BenchlingAppArchiveInterface {
  archived: boolean;
  id: string;
  name: string;
}

export function getBenchlingAppPageUrl(benchlingAppId: string | null): string | null {
  return benchlingAppId ? `${BENCHLING_APPS_WEB_URL}/${benchlingAppId}` : null;
}

export function openBenchlingAppDeveloperPage(benchlingAppId: string): void {
  const benchlingAppPageURL = `${BENCHLING_APPS_WEB_URL}/${benchlingAppId}`;
  navigateToURL(benchlingAppPageURL);
}

export function openBenchlingAppPage(benchlingAppId: string): void {
  const benchlingAppPageURL = `apps/${benchlingAppId}`;
  navigateToURL(benchlingAppPageURL);
}

export function onAppsArchive(apps: Array<BenchlingAppArchiveInterface>, refetchResults: () => void): void {
  const filteredApps = apps.filter((a) => !a.archived);

  async function archiveHandler(appIds: Array<string>, purpose: string): Promise<void> {
    await withLoadingScreen(bulkArchiveBenchlingApps(appIds, purpose), 'Archiving...')
      .then((response) => {
        notifications.success(`${pluralize(appIds.length, 'App')} successfully archived.`);
      })
      .catch(notifications.notifyError);
    refetchResults();
  }

  ArchiveModal.open({
    items: filteredApps,
    onArchive: archiveHandler,
  });
}

export async function onAppsUnarchive(
  apps: Array<BenchlingAppArchiveInterface>,
  refetchResults: () => void
): Promise<void> {
  const filteredApps = apps.filter((a) => a.archived);
  const appIds = filteredApps.map((a) => a.id);
  await bulkUnarchiveBenchlingApps(appIds)
    .then((response) => {
      notifications.success(`${pluralize(appIds.length, 'App')} successfully unarchived.`);
    })
    .catch(notifications.notifyError);
  refetchResults();
}

/**
 * compares two semantic version strings.
 * @returns positive number if a < b, 0 if a == b, and negative number if a > b
 */
export function compareVersions(a: {versionId: string}, b: {versionId: string}): number {
  return semver.compare(a.versionId, b.versionId);
}

export function getInstallationOrDefinitionId(appDefinition: AppDefinition): string {
  if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
    return appDefinition.id;
  } else {
    assert(appDefinition.installation != null, 'Expected app definition to have an associated installation');
    return appDefinition.installation.id;
  }
}
